import React, { useMemo, useState, useRef, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import './ReportingHierarchyChart.css';

const COLLAPSED_MANAGER_THRESHOLD = 5;
const VISIBLE_MANAGER_TAIL = 2;
const COLLAPSED_NODE_ID = '__collapsed_managers__';

function buildManagerChain(higherHierarchy) {
  if (!Array.isArray(higherHierarchy) || higherHierarchy.length === 0) {
    return [];
  }

  const chain = [];
  const seen = new Set();

  const walkUp = (node) => {
    if (!node || seen.has(node.user_id)) {
      return;
    }
    if (node.managers?.length) {
      node.managers.forEach(walkUp);
    }
    if (!seen.has(node.user_id)) {
      seen.add(node.user_id);
      chain.push(node);
    }
  };

  higherHierarchy.forEach(walkUp);
  return chain;
}

function getInitials(name) {
  if (!name) {
    return '?';
  }
  return name
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0])
    .join('')
    .toUpperCase();
}

function formatTeamName(teamName) {
  if (!teamName) {
    return '';
  }
  const unique = [...new Set(
    teamName.split(',').map((part) => part.trim()).filter(Boolean)
  )];
  if (unique.length === 0) {
    return '';
  }
  if (unique.length <= 2) {
    return unique.join(', ');
  }
  return `${unique.slice(0, 2).join(', ')} +${unique.length - 2}`;
}

function countDescendants(node) {
  const kids = node.reportees || node.children || [];
  return kids.reduce((sum, child) => sum + 1 + countDescendants(child), 0);
}

function getDesignation(node) {
  return (
    node.designation ||
    node.role ||
    node.title ||
    node.designation_name ||
    ''
  );
}

function toPersonNode(node, { isCurrentUser = false } = {}) {
  const reportees = node.reportees || [];
  return {
    user_id: node.user_id,
    name: node.name,
    email: node.email,
    designation: getDesignation(node),
    department_name: node.department_name || '',
    team_name: formatTeamName(node.team_name),
    isCurrentUser,
    isCollapsedPlaceholder: false,
    collapsedCount: 0,
    directCount: reportees.length,
    totalCount: countDescendants(node),
    children: [],
  };
}

function toManagerNode(manager) {
  return toPersonNode(manager);
}

function buildVisibleManagers(managerChain, expandManagers) {
  if (managerChain.length === 0) {
    return [];
  }

  const shouldCollapse =
    !expandManagers && managerChain.length > COLLAPSED_MANAGER_THRESHOLD;

  if (!shouldCollapse) {
    return managerChain.map(toManagerNode);
  }

  const hiddenCount = managerChain.length - 1 - VISIBLE_MANAGER_TAIL;
  const visible = [toManagerNode(managerChain[0])];

  if (hiddenCount > 0) {
    visible.push({
      user_id: COLLAPSED_NODE_ID,
      name: '',
      email: '',
      designation: '',
      department_name: '',
      team_name: '',
      isCurrentUser: false,
      isCollapsedPlaceholder: true,
      collapsedCount: hiddenCount,
      directCount: 0,
      totalCount: 0,
      children: [],
    });
  }

  managerChain.slice(-VISIBLE_MANAGER_TAIL).forEach((manager) => {
    visible.push(toManagerNode(manager));
  });

  return visible;
}

function buildCurrentUserNode(data, designation, department) {
  const lower = data.lower_hierarchy || [];
  return {
    user_id: data.user.user_id,
    name: data.user.name,
    email: data.user.email,
    designation: designation || getDesignation(data.user) || '',
    department_name:
      department || data.teams?.[0]?.department_name || '',
    team_name: '',
    isCurrentUser: true,
    isCollapsedPlaceholder: false,
    collapsedCount: 0,
    directCount: lower.length,
    totalCount: lower.reduce((sum, node) => sum + 1 + countDescendants(node), 0),
    children: [],
  };
}

const TreeNodeCard = ({
  node,
  youLabel,
  onExpandManagers,
  expandLabel,
  directLabel,
  totalReporteesLabel,
}) => {
  if (node.isCollapsedPlaceholder) {
    return (
      <button
        type="button"
        className="org-tree__node org-tree__node--collapsed"
        onClick={onExpandManagers}
        title={expandLabel}
      >
        <span className="org-tree__collapsed-icon">···</span>
        <span className="org-tree__collapsed-text">+{node.collapsedCount}</span>
      </button>
    );
  }

  const subtitle = [node.designation, node.department_name]
    .filter(Boolean)
    .join(' | ');

  const showCounts = node.directCount > 0 || node.totalCount > 0;

  return (
    <div
      className={`org-tree__node${node.isCurrentUser ? ' org-tree__node--current' : ''}`}
      title={node.email || undefined}
    >
      <div className="org-tree__avatar">{getInitials(node.name)}</div>
      <div className="org-tree__body">
        <div className="org-tree__name">
          {node.name}
          {node.isCurrentUser && youLabel ? (
            <span className="org-tree__you-tag">{youLabel}</span>
          ) : null}
        </div>
        {subtitle && <div className="org-tree__meta">{subtitle}</div>}
        {node.team_name && !node.isCurrentUser && (
          <div className="org-tree__meta org-tree__meta--team" title={node.team_name}>
            {node.team_name}
          </div>
        )}
        {showCounts && (
          <div className="org-tree__counts">
            {node.directCount} {directLabel} | {node.totalCount} {totalReporteesLabel}
          </div>
        )}
      </div>
    </div>
  );
};

TreeNodeCard.propTypes = {
  node: PropTypes.object.isRequired,
  youLabel: PropTypes.string,
  onExpandManagers: PropTypes.func,
  expandLabel: PropTypes.string,
  directLabel: PropTypes.string,
  totalReporteesLabel: PropTypes.string,
};

const ReportingHierarchyChart = ({
  data,
  isLoading = false,
  currentUserDesignation = '',
  currentUserDepartment = '',
  youLabel = 'You',
  showFullChainLabel = 'Show full chain',
  showLessLabel = 'Show less',
  noHierarchyLabel = 'No managers or reportees found in your hierarchy.',
  directLabel = 'Direct',
  totalReporteesLabel = 'Total Reportees',
}) => {
  const [expandManagers, setExpandManagers] = useState(false);
  const scrollXRef = useRef(null);

  const managerChain = useMemo(
    () => buildManagerChain(data?.higher_hierarchy),
    [data?.higher_hierarchy]
  );

  const visibleManagers = useMemo(
    () => buildVisibleManagers(managerChain, expandManagers),
    [managerChain, expandManagers]
  );

  const currentUserNode = useMemo(
    () =>
      data?.user
        ? buildCurrentUserNode(data, currentUserDesignation, currentUserDepartment)
        : null,
    [data, currentUserDesignation, currentUserDepartment]
  );

  const directReportees = useMemo(
    () => (data?.lower_hierarchy || []).map((node) => toPersonNode(node)),
    [data?.lower_hierarchy]
  );

  const centerReporteesScroll = useCallback(() => {
    const scroller = scrollXRef.current;
    if (!scroller) {
      return;
    }

    requestAnimationFrame(() => {
      const maxScroll = scroller.scrollWidth - scroller.clientWidth;
      if (maxScroll > 0) {
        scroller.scrollLeft = maxScroll / 2;
      }
    });
  }, []);

  useEffect(() => {
    if (directReportees.length > 0) {
      centerReporteesScroll();
    }
  }, [directReportees, expandManagers, centerReporteesScroll]);

  if (isLoading) {
    return (
      <div className="reporting-hierarchy-chart reporting-hierarchy-chart--loading">
        <div className="org-tree-viewport">
          <div className="hierarchy-loading">
            <div className="hierarchy-loading__spinner" />
            <span>Loading organization tree…</span>
          </div>
        </div>
      </div>
    );
  }

  if (!data?.user || !currentUserNode) {
    return null;
  }

  const hasManagers = managerChain.length > 0;
  const hasReportees = directReportees.length > 0;
  const hasHierarchy = hasManagers || hasReportees;
  const canCollapseManagers =
    expandManagers && managerChain.length > COLLAPSED_MANAGER_THRESHOLD;

  const cardProps = {
    youLabel,
    onExpandManagers: () => {
      setExpandManagers(true);
      setTimeout(centerReporteesScroll, 50);
    },
    expandLabel: showFullChainLabel,
    directLabel,
    totalReporteesLabel,
  };

  return (
    <div className="reporting-hierarchy-chart">
      {hasHierarchy ? (
        <>
          <div className="org-tree-viewport">
            {/* Upper hierarchy: centered + fixed (does not scroll horizontally) */}
            <div className="org-tree-self-section">
              {visibleManagers.length > 0 && (
                <ul className="org-tree org-tree--managers">
                  {visibleManagers.map((manager) => (
                    <li
                      key={manager.isCollapsedPlaceholder ? COLLAPSED_NODE_ID : manager.user_id}
                      className="org-tree__branch org-tree__branch--manager"
                    >
                      <TreeNodeCard node={manager} {...cardProps} />
                      <div className="org-tree__manager-stem" aria-hidden="true" />
                    </li>
                  ))}
                </ul>
              )}

              <div className="org-tree-self">
                <TreeNodeCard node={currentUserNode} {...cardProps} />
                {hasReportees && (
                  <div className="org-tree__self-stem" aria-hidden="true" />
                )}
              </div>
            </div>

            {/* Lower hierarchy only: horizontal scroll */}
            {hasReportees && (
              <div className="org-tree-scroll-x" ref={scrollXRef}>
                <div className="org-tree-scroll-x__inner">
                  <ul
                    className={`org-tree__children org-tree__children--reportees${
                      directReportees.length === 1 ? ' org-tree__children--reportees--single' : ''
                    }`}
                  >
                    {directReportees.map((child) => (
                      <li key={child.user_id} className="org-tree__branch">
                        <TreeNodeCard node={child} {...cardProps} />
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            )}
          </div>
          {canCollapseManagers && (
            <button
              type="button"
              className="hierarchy-toggle-btn"
              onClick={() => {
                setExpandManagers(false);
                setTimeout(centerReporteesScroll, 50);
              }}
            >
              {showLessLabel}
            </button>
          )}
        </>
      ) : (
        <p className="reporting-hierarchy-chart__empty">{noHierarchyLabel}</p>
      )}
    </div>
  );
};

ReportingHierarchyChart.propTypes = {
  data: PropTypes.shape({
    user: PropTypes.object,
    teams: PropTypes.array,
    higher_hierarchy: PropTypes.array,
    lower_hierarchy: PropTypes.array,
  }),
  isLoading: PropTypes.bool,
  currentUserDesignation: PropTypes.string,
  currentUserDepartment: PropTypes.string,
  youLabel: PropTypes.string,
  showFullChainLabel: PropTypes.string,
  showLessLabel: PropTypes.string,
  noHierarchyLabel: PropTypes.string,
  directLabel: PropTypes.string,
  totalReporteesLabel: PropTypes.string,
};

export default ReportingHierarchyChart;
